import React, { useState, useEffect, FC } from 'react'
import { Button, Input, Typography, message } from 'antd'
import Form, { FormComponentProps } from 'antd/lib/form'
import { useHistory } from 'react-router-dom'

import { inviteService, userService } from '../../../services'
import { UserInvite } from '../../../types'
import RequestError from '../../../services/RequestError'
import styles from './index.module.less'

const { Title } = Typography

const InviteForm: FC<FormComponentProps> = props => {
	const history = useHistory()
	const [invite, setInvite] = useState<UserInvite>({ email: '' })
	const [token, setToken] = useState<string>('')
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const passwordValidationMessage =
		'Password must be a minimum of 8 characters and contain at least one lowercase letter, one uppercase letter and one number.'
	const claimErrorMessage = 'An unexpected error occured when trying to claim your invite. Please try again.'

	const onSubmit: React.FormEventHandler<HTMLFormElement> = async (event): Promise<void> => {
		event.preventDefault()
		setIsLoading(true)
		props.form.validateFields(
			async (errors, values): Promise<void> => {
				if (errors) {
					setIsLoading(false)
					return
				}

				try {
					await inviteService.claim(token, { password: values.password, display_name: values.display_name })
					window.location.assign(userService.loginUrl(`/${window.location.hash}`))
				} catch (e) {
					const error = e as RequestError
					if (error.response?.status === 422) {
						message.warn(passwordValidationMessage)
					} else {
						message.error(claimErrorMessage)
					}
				} finally {
					setIsLoading(false)
				}
			}
		)
	}

	useEffect(() => {
		const getInvite = async (): Promise<void> => {
			const urlParams = new URLSearchParams(window.location.hash.split('?')[1])
			const token = urlParams.get('token')
			try {
				const { data } = await inviteService.get(token)
				setToken(token)
				setInvite(data)
				setIsLoading(false)
			} catch (error) {
				if ((error as RequestError).response?.status !== 410) {
					message.error(claimErrorMessage)
					return
				}

				history.push('/')
			}
		}

		getInvite()
	}, [])

	return (
		<Form onSubmit={onSubmit}>
			<div className={styles.container}>
				<Title className={styles.title}>Licensing Portal</Title>
				<div className={styles.main}>
					<Form.Item>
						<Input size='large' value={invite.email} placeholder='' disabled />
					</Form.Item>
					<Form.Item>
						{props.form.getFieldDecorator('display_name', {
							rules: [
								{
									whitespace: true,
									required: true,
									message: 'Name is required.'
								}
							]
						})(<Input placeholder='Enter name' type='text' disabled={isLoading} size='large' />)}
					</Form.Item>
					<Form.Item>
						{props.form.getFieldDecorator('password', {
							rules: [
								{
									whitespace: true,
									required: true,
									message: 'Password is required.'
								},
								{
									pattern: /^(?=.*\d)(?=.*[A-Z])([^\s]){8,}$/,
									message: passwordValidationMessage
								}
							]
						})(<Input size='large' placeholder='Enter password' disabled={isLoading} type='password' />)}
					</Form.Item>
					<Button loading={isLoading} htmlType='submit' type='primary' size='large' className={styles.button}>
						Register
					</Button>
				</div>
			</div>
		</Form>
	)
}

export default Form.create()(InviteForm)
